import React, { useState, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import axios from 'axios';
import API_BASE_URL from '../config';
import './AllModal.css';

const AddProposalRecModal = ({ proposalId, proposal, onRecAdded, onClose }) => {
  const [recType, setRecType] = useState('things-to-do');
  const [error, setError] = useState('');
  const modalRef = useRef(null);
  const [destination, setDestination] = useState(proposal?.destinations?.[0] || '');
  
  // Restaurant-specific fields
  const [restaurantName, setRestaurantName] = useState('');
  const [restaurantType, setRestaurantType] = useState('');
  const [cuisineType, setCuisineType] = useState('');
  const [googleMapsUrl, setGoogleMapsUrl] = useState('');
  const [instagramLink, setInstagramLink] = useState('');
  
  // Activity-specific fields
  const [activityTitle, setActivityTitle] = useState('');
  const [activityType, setActivityType] = useState('');
  const [activityUrl, setActivityUrl] = useState('');
  const [timesAvailable, setTimesAvailable] = useState([]);
  const [timeInput, setTimeInput] = useState('');
  const [costMin, setCostMin] = useState('');
  const [costMax, setCostMax] = useState('');
  const [activityNotes, setActivityNotes] = useState('');
  
  // Event-specific fields
  const [eventTitle, setEventTitle] = useState('');
  const [eventType, setEventType] = useState('');
  const [eventDateTime, setEventDateTime] = useState(null);
  const [venue, setVenue] = useState('');
  const [eventCostMin, setEventCostMin] = useState('');
  const [eventCostMax, setEventCostMax] = useState('');
  const [eventNotes, setEventNotes] = useState('');
  
  // Things to Do specific fields
  const [thingsToDoTitle, setThingsToDoTitle] = useState('');
  const [timeDuration, setTimeDuration] = useState('');
  const [thingsToDoUrl, setThingsToDoUrl] = useState('');
  const [mapLink, setMapLink] = useState('');

  // Helper functions for times available
  const validateTimeFormat = (time) => {
    // Validate 24-hour format (HH:MM)
    const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return timeRegex.test(time);
  };

  const addTimeSlot = () => {
    const trimmedTime = timeInput.trim();
    if (trimmedTime && validateTimeFormat(trimmedTime)) {
      if (!timesAvailable.includes(trimmedTime)) {
        setTimesAvailable([...timesAvailable, trimmedTime].sort());
        setTimeInput('');
      } else {
        setError('This time is already added');
        setTimeout(() => setError(''), 3000);
      }
    } else if (trimmedTime) {
      setError('Please enter time in 24-hour format (HH:MM, e.g., 09:30, 14:00)');
      setTimeout(() => setError(''), 3000);
    }
  };

  const removeTimeSlot = (index) => {
    const newTimes = timesAvailable.filter((_, i) => i !== index);
    setTimesAvailable(newTimes);
  };

  const handleSave = async () => {
    try {
      setError('');
      
      const recommendationData = {
        recType,
        destination: destination || null,
        ...(recType === 'things-to-do' && {
          thingsToDoTitle,
          timeDuration,
          thingsToDoUrl,
          mapLink
        }),
        ...(recType === 'restaurants' && {
          restaurantName,
          restaurantType,
          cuisineType,
          googleMapsUrl,
          instagramLink
        }),
        ...(recType === 'activities' && {
          activityTitle,
          activityType,
          activityUrl,
          timesAvailable: timesAvailable.filter(time => time.trim() !== ''),
          costMin,
          costMax,
          activityNotes
        }),
        ...(recType === 'events' && {
          eventTitle,
          eventType,
          eventDateTime,
          venue,
          eventCostMin,
          eventCostMax,
          eventNotes
        })
      };
      
      console.log('Saving recommendation:', recommendationData);
      
      // Make API call to add recommendation to proposal
      const response = await axios.post(`${API_BASE_URL}/api/proposals/${proposalId}/recommendations`, recommendationData);
      
      if (response.data) {
        console.log('Recommendation saved successfully:', response.data);
        onRecAdded && onRecAdded(response.data);
        onClose && onClose();
      }
    } catch (error) {
      console.error('Error saving recommendation:', error);
      setError(error.response?.data?.error || 'Failed to save recommendation');
    }
  };

  const handleCancel = () => {
    onClose && onClose();
  };

  // Handle click outside modal
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.className === 'modal-overlay') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  return (
    <div className="add-proposal-rec-modal">
      <div className="modal-overlay">
        <div className="modal" ref={modalRef}>
          <h3>Add Recommendation</h3>
          
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-container">
            <div className="form-group">
              <label>Rec Type</label>
              <select
                className="form-input"
                value={recType}
                onChange={(e) => setRecType(e.target.value)}
              >
                <option value="things-to-do">Things to Do</option>
                <option value="restaurants">Restaurants</option>
                <option value="activities">Activities</option>
                <option value="events">Events</option>
              </select>
            </div>

            <div className="form-group">
              <label>Destination</label>
              <select
                className="form-input"
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
              >
                <option value="">Select Destination</option>
                {proposal?.destinations?.map((dest, index) => (
                  <option key={index} value={dest}>{dest}</option>
                ))}
              </select>
            </div>

            {/* Things to Do specific fields */}
            {recType === 'things-to-do' && (
              <>
                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    className="form-input"
                    value={thingsToDoTitle}
                    onChange={(e) => setThingsToDoTitle(e.target.value)}
                    placeholder="Enter attraction or activity title"
                  />
                </div>

                <div className="form-group">
                  <label>Time</label>
                  <select
                    className="form-input"
                    value={timeDuration}
                    onChange={(e) => setTimeDuration(e.target.value)}
                  >
                    <option value="">Select Duration</option>
                    <option value="few-hours">Few Hours</option>
                    <option value="half-day">Half Day</option>
                    <option value="full-day">Full Day</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>URL</label>
                  <input
                    type="url"
                    className="form-input"
                    value={thingsToDoUrl}
                    onChange={(e) => setThingsToDoUrl(e.target.value)}
                    placeholder="https://example.com/..."
                  />
                </div>

                <div className="form-group">
                  <label>Map Link</label>
                  <input
                    type="url"
                    className="form-input"
                    value={mapLink}
                    onChange={(e) => setMapLink(e.target.value)}
                    placeholder="https://maps.google.com/..."
                  />
                </div>
              </>
            )}

            {/* Restaurant-specific fields */}
            {recType === 'restaurants' && (
              <>
                <div className="form-group">
                  <label>Restaurant Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={restaurantName}
                    onChange={(e) => setRestaurantName(e.target.value)}
                    placeholder="Enter restaurant name"
                  />
                </div>

                <div className="form-group">
                  <label>Restaurant Type</label>
                  <select
                    className="form-input"
                    value={restaurantType}
                    onChange={(e) => setRestaurantType(e.target.value)}
                  >
                    <option value="">Select Type</option>
                    <option value="breakfast">Breakfast</option>
                    <option value="brunch">Brunch</option>
                    <option value="lunch">Lunch</option>
                    <option value="dinner">Dinner</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Cuisine Type</label>
                  <input
                    type="text"
                    className="form-input"
                    value={cuisineType}
                    onChange={(e) => setCuisineType(e.target.value)}
                    placeholder="e.g., Italian, Mexican, Asian, etc."
                  />
                </div>

                <div className="form-group">
                  <label>Google Maps URL</label>
                  <input
                    type="url"
                    className="form-input"
                    value={googleMapsUrl}
                    onChange={(e) => setGoogleMapsUrl(e.target.value)}
                    placeholder="https://maps.google.com/..."
                  />
                </div>

                <div className="form-group">
                  <label>Instagram Link</label>
                  <input
                    type="url"
                    className="form-input"
                    value={instagramLink}
                    onChange={(e) => setInstagramLink(e.target.value)}
                    placeholder="https://instagram.com/..."
                  />
                </div>
              </>
            )}

            {/* Activity-specific fields */}
            {recType === 'activities' && (
              <>
                <div className="form-group">
                  <label>Activity Title</label>
                  <input
                    type="text"
                    className="form-input"
                    value={activityTitle}
                    onChange={(e) => setActivityTitle(e.target.value)}
                    placeholder="Enter activity title"
                  />
                </div>

                <div className="form-group">
                  <label>Activity Type</label>
                  <select
                    className="form-input"
                    value={activityType}
                    onChange={(e) => setActivityType(e.target.value)}
                  >
                    <option value="">Select Type</option>
                    <option value="tour">Tour</option>
                    <option value="museum">Museum</option>
                    <option value="spa">SPA</option>
                    <option value="hike">Hike</option>
                    <option value="class">Class</option>
                    <option value="wine-tasting">Wine Tasting</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>URL</label>
                  <input
                    type="url"
                    className="form-input"
                    value={activityUrl}
                    onChange={(e) => setActivityUrl(e.target.value)}
                    placeholder="https://example.com/..."
                  />
                </div>

                <div className="form-group">
                  <label>Times Available (24-hour format)</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                    <input
                      className="form-input"
                      type="text"
                      value={timeInput}
                      onChange={(e) => setTimeInput(e.target.value)}
                      placeholder="Enter time (HH:MM)"
                      style={{ flex: 1 }}
                      autoComplete="off"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addTimeSlot();
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={addTimeSlot}
                      className="add-button"
                      title="Add time slot"
                    >
                      +
                    </button>
                  </div>
                  
                  {/* Display added times */}
                  {timesAvailable.length > 0 && (
                    <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {timesAvailable.map((time, index) => (
                        <span key={index} style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '4px 8px',
                          backgroundColor: '#e9ecef',
                          borderRadius: '4px',
                          fontSize: '0.85em',
                          color: '#333',
                          border: '1px solid #dee2e6'
                        }}>
                          {time}
                          <button
                            type="button"
                            onClick={() => removeTimeSlot(index)}
                            style={{
                              marginLeft: '6px',
                              padding: '0 2px',
                              backgroundColor: 'transparent',
                              border: 'none',
                              color: '#6c757d',
                              cursor: 'pointer',
                              fontSize: '1.1em',
                              lineHeight: 1,
                              fontWeight: 'bold'
                            }}
                            title="Remove time slot"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>Cost Range</label>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <input
                      type="number"
                      className="form-input"
                      value={costMin}
                      onChange={(e) => setCostMin(e.target.value)}
                      placeholder="Min"
                      style={{ flex: 1 }}
                    />
                    <span>to</span>
                    <input
                      type="number"
                      className="form-input"
                      value={costMax}
                      onChange={(e) => setCostMax(e.target.value)}
                      placeholder="Max"
                      style={{ flex: 1 }}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    className="form-input"
                    value={activityNotes}
                    onChange={(e) => setActivityNotes(e.target.value)}
                    placeholder="Enter any additional notes about the activity"
                    rows="3"
                    style={{ resize: 'vertical' }}
                  />
                </div>
              </>
            )}

            {/* Event-specific fields */}
            {recType === 'events' && (
              <>
                <div className="form-group">
                  <label>Event Title</label>
                  <input
                    type="text"
                    className="form-input"
                    value={eventTitle}
                    onChange={(e) => setEventTitle(e.target.value)}
                    placeholder="Enter event title"
                  />
                </div>

                <div className="form-group">
                  <label>Event Type</label>
                  <select
                    className="form-input"
                    value={eventType}
                    onChange={(e) => setEventType(e.target.value)}
                  >
                    <option value="">Select Type</option>
                    <option value="concert">Concert</option>
                    <option value="sports-game">Sports Game</option>
                    <option value="theater">Theater</option>
                    <option value="festival">Festival</option>
                    <option value="conference">Conference</option>
                    <option value="exhibition">Exhibition</option>
                    <option value="workshop">Workshop</option>
                    <option value="party">Party</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Date & Time</label>
                  <DatePicker
                    selected={eventDateTime}
                    onChange={date => setEventDateTime(date)}
                    dateFormat="MMMM d, yyyy h:mm aa"
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    timeCaption="Time"
                    className="form-input"
                    placeholderText="Select date and time"
                    minDate={proposal?.startDate ? new Date(proposal.startDate) : null}
                    maxDate={proposal?.endDate ? new Date(proposal.endDate) : null}
                    filterDate={(date) => {
                      if (!proposal?.startDate || !proposal?.endDate) return true;
                      const startDate = new Date(proposal.startDate);
                      const endDate = new Date(proposal.endDate);
                      return date >= startDate && date <= endDate;
                    }}
                  />
                </div>

                <div className="form-group">
                  <label>Venue</label>
                  <input
                    type="text"
                    className="form-input"
                    value={venue}
                    onChange={(e) => setVenue(e.target.value)}
                    placeholder="Enter venue name and location"
                  />
                </div>

                <div className="form-group">
                  <label>Cost Range</label>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <input
                      type="number"
                      className="form-input"
                      value={eventCostMin}
                      onChange={(e) => setEventCostMin(e.target.value)}
                      placeholder="Min"
                      style={{ flex: 1 }}
                    />
                    <span>to</span>
                    <input
                      type="number"
                      className="form-input"
                      value={eventCostMax}
                      onChange={(e) => setEventCostMax(e.target.value)}
                      placeholder="Max"
                      style={{ flex: 1 }}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    className="form-input"
                    value={eventNotes}
                    onChange={(e) => setEventNotes(e.target.value)}
                    placeholder="Enter any additional notes about the event"
                    rows="3"
                    style={{ resize: 'vertical' }}
                  />
                </div>
              </>
            )}
          </div>
          
          <div className="modal-actions">
            <button className="modal-action-button" onClick={handleCancel}>Cancel</button>
            <button className="modal-action-button" onClick={handleSave}>Add</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddProposalRecModal;